import { Save, Trash2, X } from "lucide-react";
import React, { useEffect, useState } from "react";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "src/components/ui/alert-dialog";
import { AnimatedDialog, AnimatedDialogContent, AnimatedDialogHeader, AnimatedDialogTitle } from "src/components/ui/animated-dialog";
import { Button } from "src/components/ui/button";
import { Input } from "src/components/ui/input";
import { InvalidFieldTooltip } from "src/components/ui/invalid-field-tooltip";
import { Label } from "src/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "src/components/ui/select";
import { UITourBanner } from "src/components/ui/UITourBanner";
import { useFeatureFlag } from "src/hooks/use-feature-flag";
import { useScrollToFirstInvalid } from "src/hooks/use-scroll-to-first-invalid";
import { useToast } from "src/hooks/use-toast";

import type { DigestReasoningType } from "../CreateDigestWizard/types";
import type { CreateJobMutation as CreateJobMutationType } from "../mutations/__generated__/CreateJobMutation.graphql";
import type { DeleteJobMutation as DeleteJobMutationType } from "../mutations/__generated__/DeleteJobMutation.graphql";
import type { UpdateJobMutation as UpdateJobMutationType } from "../mutations/__generated__/UpdateJobMutation.graphql";
import { CreateJobMutation } from "../mutations/CreateJobMutation";
import { DeleteJobMutation } from "../mutations/DeleteJobMutation";
import { UpdateJobMutation } from "../mutations/UpdateJobMutation";
import type { EditDigestJobDialogQuery as EditDigestJobDialogQueryType } from "./__generated__/EditDigestJobDialogQuery.graphql";
import type { EditDigestJobDialogValidateQuery as EditDigestJobDialogValidateQueryType } from "./__generated__/EditDigestJobDialogValidateQuery.graphql";
import CompactSectionBuilder from "./CompactSectionBuilder";
import { DigestNotificationDestinations } from "./DigestNotificationDestinations";
import { FormSection } from "./FormSection";
import { ReasoningPicker } from "./ReasoningPicker";
import ScheduleBuilder from "./ScheduleBuilder";

export const EditDigestJobDialogQuery = graphql`
  query EditDigestJobDialogQuery($jobId: GlobalID!) {
    node(id: $jobId) {
      ... on GraphQLJob {
        id
        name
        inputJson
        schedule
        notificationIntegrations {
          id
          name
          kind
          slackChannelId
        }
      }
    }
  }
`;

const validateDigestIntegrationsQuery = graphql`
  query EditDigestJobDialogValidateQuery {
    validateDigestIntegrations {
      isValid
      hasMonitoring
      hasGithub
      missingIntegrations
      defaultContent {
        section
        text
      }
    }
  }
`;

const getOrganizationIntegrationsQuery = graphql`
  query EditDigestJobDialogIntegrationsQuery($organizationId: GlobalID!) {
    node(id: $organizationId) {
      ... on GraphQLOrganization {
        id
        integrations {
          edges {
            node {
              integrationType
              integrationStatus
            }
          }
        }
        slackChannels: notificationIntegrations(kind: SLACK_CHANNEL) {
          edges {
            node {
              id
              name
              slackChannelId
            }
          }
        }
        discordWebhooks: notificationIntegrations(kind: DISCORD_WEBHOOK) {
          edges {
            node {
              id
              name
            }
          }
        }
      }
    }
    slackChannelsForDisplay: slackChannels(types: "public_channel,private_channel") {
      id
      name
      isMember
      isPrivate
    }
  }
`;

interface DigestSection {
  id: string;
  name: string;
  prompt: string;
}

interface EditDigestJobDialogProps {
  jobId?: string | null;
  isOpen: boolean;
  onClose: () => void;
  teamId?: string | null;
  organizationId: string;
  initialSections?: DigestSection[];
  onSuccess?: (jobId?: string) => void;
  isOnboarding?: boolean;
}

export default function EditDigestJobDialog({
  jobId,
  isOpen,
  onClose,
  teamId,
  organizationId,
  initialSections,
  onSuccess,
  isOnboarding = false,
}: EditDigestJobDialogProps) {
  const { toast } = useToast();
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const isEditMode = !!jobId;

  // Fetch organization integrations
  const integrationsData = useLazyLoadQuery<any>(
    getOrganizationIntegrationsQuery,
    { organizationId },
    { fetchPolicy: "store-or-network" }
  );

  const organization = integrationsData?.node;

  const hasSlackIntegration =
    organization?.integrations?.edges?.some(
      (edge: any) => edge.node.integrationType === "SLACK" && edge.node.integrationStatus === "APPROVED"
    ) ?? false;

  const hasDiscordIntegration =
    organization?.integrations?.edges?.some(
      (edge: any) => edge.node.integrationType === "DISCORD" && edge.node.integrationStatus === "APPROVED"
    ) ?? false;

  const discordWebhooks = organization?.discordWebhooks?.edges || [];
  const allSlackChannels = integrationsData?.slackChannelsForDisplay || [];

  const jobData = isEditMode
    ? useLazyLoadQuery<EditDigestJobDialogQueryType>(EditDigestJobDialogQuery, { jobId: jobId as string }, { fetchPolicy: "network-only" })
    : null;

  const job = isEditMode ? jobData?.node : null;

  const validationData = isOnboarding
    ? useLazyLoadQuery<EditDigestJobDialogValidateQueryType>(validateDigestIntegrationsQuery, {}, { fetchPolicy: "store-and-network" })
    : null;

  // Default values for create mode
  const getDefaultSections = () => {
    if (isOnboarding) {
      const defaultContent = validationData?.validateDigestIntegrations?.defaultContent;
      if (defaultContent && defaultContent.length > 0) {
        return defaultContent.map((item) => ({
          section: item.section,
          text: item.text,
        }));
      }
      return [{ section: "", text: "" }];
    } else if (initialSections && initialSections.length > 0) {
      return initialSections.filter((s) => s.name.trim() || s.prompt.trim()).map((s) => ({ section: s.name, text: s.prompt }));
    } else {
      return [{ section: "", text: "" }];
    }
  };

  const defaultInputJson = {
    sections: getDefaultSections(),
    slack_channel_id: "",
  };

  const defaultSchedule = isOnboarding ? { cron_expression: "0 9 * * *" } : { cron_expression: "0 0 * * 1" };

  // Normalize the inputJson format for display
  const normalizeInputJson = (rawInputJson: any) => {
    const result: { sections: Array<{ section: string; text: string }>; slack_channel_id: string } = {
      sections: [],
      slack_channel_id: "",
    };

    if (typeof rawInputJson === "object") {
      if (rawInputJson.sections && Array.isArray(rawInputJson.sections)) {
        result.sections = rawInputJson.sections;
        result.slack_channel_id = rawInputJson.slack_channel_id || "";
      } else if (Array.isArray(rawInputJson)) {
        result.sections = rawInputJson as Array<{ section: string; text: string }>;
      } else if (rawInputJson.input && Array.isArray(rawInputJson.input)) {
        result.sections = rawInputJson.input as Array<{ section: string; text: string }>;
      }
    }

    if (!result.sections || result.sections.length === 0) {
      result.sections = [{ section: "", text: "" }];
    }

    return result;
  };

  const normalizedInputJson = isEditMode ? normalizeInputJson(job?.inputJson) : defaultInputJson;

  // Extract existing notification integration IDs
  const existingNotificationIntegrationIds = React.useMemo(() => {
    if (!isEditMode || !job?.notificationIntegrations) return [];
    return job.notificationIntegrations.filter((ni: any) => ni.kind === "DISCORD_WEBHOOK").map((ni: any) => ni.id);
  }, [isEditMode, job?.notificationIntegrations]);

  const existingSlackChannels = React.useMemo(() => {
    if (!isEditMode || !job) return [];

    const inputJson = job.inputJson as Record<string, any> | undefined;
    const hasNotificationIntegrationIds = inputJson && "notification_integration_ids" in inputJson;

    if (job.notificationIntegrations && job.notificationIntegrations.length > 0) {
      const slackChannelNIs = job.notificationIntegrations.filter((ni: any) => ni.kind === "SLACK_CHANNEL" && ni.slackChannelId);

      return slackChannelNIs.map((ni: any) => {
        const channelData = allSlackChannels.find((ch: any) => ch.id === ni.slackChannelId);
        return {
          id: ni.slackChannelId,
          name: channelData?.name || ni.slackChannelId,
          isMember: channelData?.isMember ?? true,
        };
      });
    } else if (!hasNotificationIntegrationIds && inputJson?.slack_channel_id) {
      const channelId = inputJson.slack_channel_id;
      const channelData = allSlackChannels.find((ch: any) => ch.id === channelId);
      return [
        {
          id: channelId,
          name: channelData?.name || channelId,
          isMember: channelData?.isMember ?? true,
        },
      ];
    }

    return [];
  }, [isEditMode, job?.notificationIntegrations, job?.inputJson, allSlackChannels]);

  // Form state
  const [jobName, setJobName] = useState(isEditMode ? job?.name || "" : isOnboarding ? "Daily Error Report" : "");
  const [inputJson, setInputJson] = useState(JSON.stringify(normalizedInputJson, null, 2));
  const [selectedSlackChannels, setSelectedSlackChannels] = useState<Array<{ id: string; name: string; isMember: boolean }>>([]);
  const [schedule, setSchedule] = useState(isEditMode ? JSON.stringify(job?.schedule, null, 2) : JSON.stringify(defaultSchedule, null, 2));
  const [selectedDiscordWebhooks, setSelectedDiscordWebhooks] = useState<Array<{ id: string; name: string }>>([]);
  const [notificationFormat, setNotificationFormat] = useState<"summary" | "full">(
    isEditMode && job?.inputJson && typeof job.inputJson === "object" && "notification_format" in job.inputJson
      ? (job.inputJson.notification_format as "summary" | "full")
      : "summary"
  );
  const isReasoningPickerEnabled = useFeatureFlag("DIGEST_EFFORT_LEVEL");
  const [reasoning, setReasoning] = useState<DigestReasoningType>(
    isEditMode && job?.inputJson && typeof job.inputJson === "object" && "reasoning" in job.inputJson
      ? (job.inputJson.reasoning as DigestReasoningType)
      : "advanced"
  );

  const [, setInputJsonError] = useState(false);
  const [scheduleError, setScheduleError] = useState(false);
  const [validationErrorMessage, setValidationErrorMessage] = useState("");
  const { containerRef: scrollContainerRef, showErrors: showValidationErrors, trigger: triggerValidationScroll, reset: resetValidation } = useScrollToFirstInvalid();

  // Mutations
  const [createJob] = useMutation<CreateJobMutationType>(CreateJobMutation);
  const [updateJob] = useMutation<UpdateJobMutationType>(UpdateJobMutation);
  const [deleteJob, isDeleteInFlight] = useMutation<DeleteJobMutationType>(DeleteJobMutation);

  // Load existing data when modal opens
  React.useEffect(() => {
    if (isOpen && existingNotificationIntegrationIds.length > 0) {
      const existingWebhooks = discordWebhooks
        .filter((edge: any) => existingNotificationIntegrationIds.includes(edge.node.id))
        .map((edge: any) => ({ id: edge.node.id, name: edge.node.name }));
      setSelectedDiscordWebhooks(existingWebhooks);
    } else if (isOpen && !isEditMode) {
      setSelectedDiscordWebhooks([]);
    }
  }, [isOpen, isEditMode, existingNotificationIntegrationIds.length, discordWebhooks]);

  React.useEffect(() => {
    if (isOpen) {
      setSelectedSlackChannels(existingSlackChannels);
    }
  }, [isOpen, existingSlackChannels.length]);

  useEffect(() => {
    if (isOpen) {
      resetValidation();
      setValidationErrorMessage("");
    }
  }, [isOpen, resetValidation]);

  // Validation functions
  const validateInputJson = () => {
    try {
      const parsed = JSON.parse(inputJson);
      if (!parsed.sections || !Array.isArray(parsed.sections)) {
        setInputJsonError(true);
        return false;
      }
      if (parsed.sections.length === 0) {
        setInputJsonError(true);
        return false;
      }
      if (typeof parsed.slack_channel_id !== "string") {
        setInputJsonError(true);
        return false;
      }
      let isValid = true;
      for (const item of parsed.sections) {
        if (
          !item.section ||
          !item.text ||
          typeof item.section !== "string" ||
          typeof item.text !== "string" ||
          item.section.trim() === "" ||
          item.text.trim() === ""
        ) {
          isValid = false;
          break;
        }
      }
      if (!isValid) {
        setInputJsonError(true);
        return false;
      }
      setInputJsonError(false);
      return true;
    } catch {
      setInputJsonError(true);
      return false;
    }
  };

  const validateSchedule = () => {
    try {
      const parsed = typeof schedule === "string" ? JSON.parse(schedule) : schedule;
      const cronValue = parsed.cron_expression;
      if (!cronValue || typeof cronValue !== "string" || cronValue === "") {
        setScheduleError(true);
        return false;
      }
      setScheduleError(false);
      return true;
    } catch {
      setScheduleError(true);
      return false;
    }
  };

  const handleCancelEdit = () => {
    setInputJsonError(false);
    setScheduleError(false);
    setValidationErrorMessage("");
    resetValidation();
    onClose();
  };

  const handleSaveClick = () => {
    setInputJsonError(false);
    setScheduleError(false);
    setValidationErrorMessage("");

    const isJobNameValid = jobName.trim() !== "";
    const isInputJsonValid = validateInputJson();
    const isScheduleValid = validateSchedule();

    if (isJobNameValid && isInputJsonValid && isScheduleValid && !scheduleError) {
      setIsSaving(true);

      const parsedInputJson = JSON.parse(inputJson);
      const parsedSchedule = JSON.parse(schedule);

      if (parsedSchedule.cron && !parsedSchedule.cron_expression) {
        parsedSchedule.cron_expression = parsedSchedule.cron;
        delete parsedSchedule.cron;
      }

      if (!parsedInputJson.sections || !Array.isArray(parsedInputJson.sections)) {
        setValidationErrorMessage("Invalid input format: missing sections array");
        setIsSaving(false);
        return;
      }

      const newInputJson: Record<string, unknown> = {
        sections: parsedInputJson.sections,
        notification_format: notificationFormat,
      };
      if (isReasoningPickerEnabled) {
        newInputJson.reasoning = reasoning;
      } else if (parsedInputJson.reasoning) {
        newInputJson.reasoning = parsedInputJson.reasoning;
      }

      if (isEditMode) {
        const slackChannelIds = selectedSlackChannels.map((ch) => ch.id);
        const discordWebhookIds = selectedDiscordWebhooks.map((wh) => wh.id);

        updateJob({
          variables: {
            input: {
              jobId: job?.id,
              name: jobName,
              inputJson: newInputJson,
              schedule: parsedSchedule,
              slackChannelIds: slackChannelIds,
              notificationIntegrationIds: discordWebhookIds,
            },
          },
          onCompleted: (response) => {
            setIsSaving(false);
            if (response.updateJob?.job) {
              toast({
                title: "Digest Agent updated",
                description: "The digest agent has been successfully updated.",
                variant: "success",
              });
              onClose();
            } else {
              toast({
                title: "Error updating digest agent",
                description: "The digest agent couldn't be updated. Please try again.",
                variant: "error",
              });
            }
          },
          onError: (error) => {
            setIsSaving(false);
            toast({
              title: "Error updating digest agent",
              description: error.message || "An unexpected error occurred",
              variant: "error",
            });
          },
        });
      } else {
        if (!teamId) {
          setIsSaving(false);
          toast({
            title: "Error creating digest agent",
            description: "Team ID is required to create a new digest agent.",
            variant: "error",
          });
          return;
        }

        const slackChannelIds = selectedSlackChannels.map((ch) => ch.id);
        const discordWebhookIds = selectedDiscordWebhooks.map((wh) => wh.id);

        createJob({
          variables: {
            input: {
              teamId,
              name: jobName,
              inputJson: newInputJson,
              schedule: parsedSchedule,
              taskType: "DIGEST",
              slackChannelIds: slackChannelIds,
              notificationIntegrationIds: discordWebhookIds,
            },
          },
          onCompleted: (response) => {
            setIsSaving(false);
            if (response.createJob?.job) {
              toast({
                title: "Digest Agent created",
                description: "The digest agent has been successfully created.",
                variant: "success",
              });
              if (onSuccess) {
                onSuccess(response.createJob.job.id);
              } else {
                onClose();
              }
            } else {
              toast({
                title: "Error creating digest agent",
                description: "The digest agent couldn't be created. Please try again.",
                variant: "error",
              });
            }
          },
          onError: (error) => {
            setIsSaving(false);
            toast({
              title: "Error creating digest agent",
              description: error.message || "An unexpected error occurred",
              variant: "error",
            });
          },
        });
      }
    } else {
      let errorMessage = "Please fix the following errors:";
      if (!isJobNameValid) {
        errorMessage += "\n- Digest name is required";
      }
      if (!isInputJsonValid) {
        errorMessage += "\n- All sections must have a name and prompt";
      }
      if (!isScheduleValid) {
        errorMessage += "\n- Please set a valid cron expression for the schedule";
      }
      setValidationErrorMessage(errorMessage);
      triggerValidationScroll();
    }
  };

  const handleDeleteClick = () => {
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    deleteJob({
      variables: {
        input: {
          jobId: job?.id,
        },
      },
      onCompleted: (response: any) => {
        if (response.deleteJob?.success) {
          toast({
            title: "Digest Agent deleted",
            description: "The digest agent has been successfully deleted.",
            variant: "success",
          });
          window.location.href = "/digests";
        } else {
          toast({
            title: "Error",
            description: "Failed to delete the digest agent. Please try again.",
            variant: "error",
          });
        }
        setIsDeleteDialogOpen(false);
      },
      onError: (error) => {
        toast({
          title: "Error",
          description: error.message || "An unexpected error occurred",
          variant: "error",
        });
        setIsDeleteDialogOpen(false);
      },
    });
  };

  const nameInvalid = showValidationErrors && jobName.trim() === "";

  return (
    <AnimatedDialog open={isOpen} onOpenChange={onClose}>
      <AnimatedDialogContent variant="full" dismissOnOutsideClick={false}>
        <AnimatedDialogHeader className="pb-4 border-b border-border">
          <AnimatedDialogTitle className="text-foreground text-xl">
            {isEditMode ? "Edit Digest Agent" : "Create Digest Agent"}
          </AnimatedDialogTitle>
        </AnimatedDialogHeader>

        {isOnboarding && (
          <UITourBanner
            text="Let's create your first digest agent - a daily error report for your favorite service. Fill in the service name in the 'Error Analysis' section below, and then click 'Create'."
            className="mt-4 w-full p-4"
            showBackButton={false}
          />
        )}

        <div ref={scrollContainerRef} className="mt-4">
          {validationErrorMessage && (
            <div className="sticky top-0 z-10 text-red-400 p-2 mb-4 border border-red-800 rounded-md whitespace-pre-line bg-background">
              {validationErrorMessage}
            </div>
          )}
          <div className="space-y-5">
            <FormSection number={1} title="Name" description={isEditMode ? "" : "Give your digest agent a name to identify it."}>
              <InvalidFieldTooltip invalid={nameInvalid} message="Digest name is required">
                <Input
                  id="digest-name"
                  value={jobName}
                  onChange={(e) => setJobName(e.target.value)}
                  placeholder="e.g. Daily Error Report"
                  aria-invalid={nameInvalid}
                  className={`bg-background border-border text-foreground ${nameInvalid ? "border-red-500" : ""}`}
                />
              </InvalidFieldTooltip>
            </FormSection>

            <div className="border-t border-border/50" />

            <FormSection number={2} title="Sections" description="Define what the agent should investigate and report on.">
              <CompactSectionBuilder
                value={inputJson}
                onChange={setInputJson}
                onError={(error) => setInputJsonError(error)}
                showValidationErrors={showValidationErrors}
              />
            </FormSection>

            <div className="border-t border-border/50" />

            <FormSection number={3} title="Schedule" description="Set when your digest should run.">
              <ScheduleBuilder value={schedule} onChange={setSchedule} onError={(error) => setScheduleError(error)} />
            </FormSection>

            <div className="border-t border-border/50" />

            <FormSection number={4} title="Delivery" description="Choose where to send digest results.">
              <DigestNotificationDestinations
                hasSlackIntegration={hasSlackIntegration}
                hasDiscordIntegration={hasDiscordIntegration}
                selectedSlackChannels={selectedSlackChannels}
                selectedDiscordWebhooks={selectedDiscordWebhooks}
                availableDiscordWebhooks={discordWebhooks.map((edge: any) => ({
                  id: edge.node.id,
                  name: edge.node.name,
                }))}
                onSlackChannelAdd={(channel) => {
                  setSelectedSlackChannels([...selectedSlackChannels, channel]);
                }}
                onSlackChannelRemove={(channelId) => {
                  setSelectedSlackChannels(selectedSlackChannels.filter((ch) => ch.id !== channelId));
                }}
                onDiscordWebhookAdd={(webhook) => {
                  setSelectedDiscordWebhooks([...selectedDiscordWebhooks, webhook]);
                }}
                onDiscordWebhookRemove={(webhookId) => {
                  setSelectedDiscordWebhooks(selectedDiscordWebhooks.filter((wh) => wh.id !== webhookId));
                }}
                noIntegrationsVariant="banner"
              />
              <div className="mt-4">
                <Label className="text-sm text-muted-foreground mb-2 block">Format</Label>
                <Select
                  value={notificationFormat}
                  onValueChange={(value: "summary" | "full") => setNotificationFormat(value)}
                >
                  <SelectTrigger className="w-48 bg-background border-border">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="summary">Summary</SelectItem>
                    <SelectItem value="full">Full Content</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </FormSection>

            {isReasoningPickerEnabled && (
              <>
                <div className="border-t border-border/50" />
                <FormSection number={5} title="Reasoning" description="Level of reasoning applied to each section.">
                  <ReasoningPicker value={reasoning} onChange={setReasoning} />
                </FormSection>
              </>
            )}
          </div>
        </div>

        <div className="flex justify-between mt-6">
          {isEditMode ? (
            <Button
              onClick={handleDeleteClick}
              disabled={isSaving || isDeleteInFlight}
              className="bg-red-600 hover:bg-red-700 text-white border-red-600"
            >
              <Trash2 className="mr-1" size={16} />
              Delete Digest Agent
            </Button>
          ) : (
            <div></div>
          )}

          <div className="flex space-x-3">
            <Button variant="outline" onClick={handleCancelEdit} disabled={isSaving || isDeleteInFlight}>
              <X className="mr-1" size={16} />
              Cancel
            </Button>
            <Button onClick={handleSaveClick} disabled={isSaving || isDeleteInFlight}>
              {isSaving ? (
                <>
                  <div className="animate-spin w-4 h-4 border-2 border-current border-t-transparent rounded-full mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="mr-1" size={16} />
                  {isEditMode ? "Save" : "Create"}
                </>
              )}
            </Button>
          </div>
        </div>
      </AnimatedDialogContent>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent className="bg-popover text-foreground border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-foreground">Confirm deletion</AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground">
              Are you sure you want to delete "{jobName || "this digest"}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="bg-transparent text-foreground border-border hover:bg-card hover:text-foreground mt-2 sm:mt-0"
              disabled={isDeleteInFlight}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700 text-white" onClick={handleConfirmDelete} disabled={isDeleteInFlight}>
              {isDeleteInFlight ? (
                <>
                  <div className="animate-spin w-4 h-4 border-2 border-current border-t-transparent rounded-full mr-2" />
                  Deleting...
                </>
              ) : (
                <>Delete</>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AnimatedDialog>
  );
}
